import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Button } from './ui/button';
import { GitBranch, AlertCircle, X, RefreshCw, SlidersHorizontal } from 'lucide-react';
import SegmentsPanel from './SegmentsPanel';
import ClusterValuesSheet from './ClusterValuesSheet';
import { type SegmentFilters } from '../api/segments';

// API imports
import {
  fetchOverview,
  fetchNextSteps,
  fetchPathNextSteps,
  fetchStepExamples,
  fetchClusters,
  fetchDropoffDeepDive,
} from '../api/journeys';
import type { ClusterListItem } from '../api/journeys';

// Type imports
import type {
  OverviewData,
  NextStepsData,
  StepExamplesData,
  DropoffDeepDiveData,
  ClusterSummary,
  TimeRangeOption,
} from '../types/journeys';
import { TIME_RANGE_OPTIONS } from '../types/journeys';

// Component imports
import OverviewSection from './journeys/OverviewSection';
import TopicExplorer from './journeys/TopicExplorer';
import ExamplesDrawer from './journeys/ExamplesDrawer';
import DropoffDrawer from './journeys/DropoffDrawer';
import AdvancedSettings, { CountMode } from './journeys/AdvancedSettings';

interface Project {
  id: number;
  name: string;
  api_key: string;
  organization: { id: number; name: string };
}

interface JourneysProps {
  currentProject: Project | null;
  token: string;
}

export default function Journeys({ currentProject, token }: JourneysProps) {
  // Filter state
  const [selectedKey, setSelectedKey] = useState<string>('prompt');
  const [selectedDays, setSelectedDays] = useState<TimeRangeOption>(7);
  const [availableKeys, setAvailableKeys] = useState<string[]>([]);
  const [countMode, setCountMode] = useState<CountMode>('sessions');
  const [isSegmentsPanelOpen, setIsSegmentsPanelOpen] = useState(false);
  const [segmentFilters, setSegmentFilters] = useState<SegmentFilters>({});

  // Data state
  const [overviewData, setOverviewData] = useState<OverviewData | null>(null);
  const [nextStepsData, setNextStepsData] = useState<NextStepsData | null>(null);
  const [clusters, setClusters] = useState<ClusterListItem[]>([]);
  const [explorationPath, setExplorationPath] = useState<ClusterSummary[]>([]);

  // Examples drawer state
  const [examplesDrawerOpen, setExamplesDrawerOpen] = useState(false);
  const [stepExamplesData, setStepExamplesData] = useState<StepExamplesData | null>(null);
  const [examplesLoading, setExamplesLoading] = useState(false);

  // Cluster values sheet state (for "View Examples" from hover)
  const [isClusterSheetOpen, setIsClusterSheetOpen] = useState(false);
  const [selectedClusterId, setSelectedClusterId] = useState<number | null>(null);

  // Dropoff drawer state
  const [isDropoffDrawerOpen, setIsDropoffDrawerOpen] = useState(false);
  const [dropoffDeepDiveData, setDropoffDeepDiveData] = useState<DropoffDeepDiveData | null>(null);
  const [dropoffLoading, setDropoffLoading] = useState(false);

  // Loading states
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [nextStepsLoading, setNextStepsLoading] = useState(false);
  const [clustersLoading, setClustersLoading] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Load available keys from clusters
  const loadAvailableKeys = useCallback(async () => {
    if (!currentProject) return;

    try {
      setClustersLoading(true);
      const clusterList = await fetchClusters(currentProject.id, token);
      setClusters(clusterList);

      // Extract unique keys
      const keys = Array.from(new Set(clusterList.map((c) => c.key)));
      setAvailableKeys(keys);

      // Set default key if not already set
      if (keys.length > 0 && !keys.includes(selectedKey)) {
        setSelectedKey(keys[0]);
      }
    } catch (err) {
      console.error('Error loading clusters:', err);
    } finally {
      setClustersLoading(false);
    }
  }, [currentProject, token, selectedKey]);

  // Load overview data
  const loadOverview = useCallback(async () => {
    if (!currentProject || !selectedKey) return;

    try {
      setOverviewLoading(true);
      setError(null);
      const data = await fetchOverview(currentProject.id, selectedKey, selectedDays, token);
      setOverviewData(data);
    } catch (err: any) {
      console.error('Error loading overview:', err);
      setError(err.message || 'Failed to load overview data');
    } finally {
      setOverviewLoading(false);
    }
  }, [currentProject, selectedKey, selectedDays, token]);

  // Load next steps for a path of clusters
  // When path has 2+ clusters, uses path-based API to show what users do AFTER following the exact path
  const loadNextSteps = useCallback(
    async (path: ClusterSummary[]) => {
      if (!currentProject || !selectedKey || path.length === 0) return;

      try {
        setNextStepsLoading(true);
        
        if (path.length === 1) {
          // Single cluster: use simple next steps
          const data = await fetchNextSteps(
            currentProject.id,
            path[0].id,
            selectedKey,
            selectedDays,
            token
          );
          setNextStepsData(data);
        } else {
          // Multiple clusters: use path-based query
          const pathIds = path.map(c => c.id);
          const pathData = await fetchPathNextSteps(
            currentProject.id,
            pathIds,
            selectedKey,
            selectedDays,
            token
          );
          
          // Convert to NextStepsData format for compatibility
          setNextStepsData({
            current_topic: path[path.length - 1],
            next_steps: pathData.next_steps,
            return_to_topic_count: pathData.return_to_path_end_count,
            total_conversations_with_topic: pathData.path_followers_count,
            ended_conversation_count: pathData.ended_conversation_count,
            continuation_percentage: pathData.continuation_percentage,
          });
        }
      } catch (err: any) {
        console.error('Error loading next steps:', err);
        setError(err.message || 'Failed to load next steps');
      } finally {
        setNextStepsLoading(false);
      }
    },
    [currentProject, selectedKey, selectedDays, token]
  );

  // Handle topic selection
  const handleSelectTopic = useCallback(
    (cluster: ClusterSummary) => {
      const newPath = [...explorationPath, cluster];
      setExplorationPath(newPath);
      loadNextSteps(newPath);
    },
    [explorationPath, loadNextSteps]
  );

  // Handle navigation to path index
  const handleNavigateToPathIndex = useCallback(
    (index: number) => {
      const newPath = explorationPath.slice(0, index + 1);
      setExplorationPath(newPath);
      loadNextSteps(newPath);
    },
    [explorationPath, loadNextSteps]
  );

  // Handle clear path
  const handleClearPath = useCallback(() => {
    setExplorationPath([]);
    setNextStepsData(null);
  }, []);

  // Handle show examples
  const handleShowExamples = useCallback(
    async (fromClusterId: number, toClusterId: number) => {
      if (!currentProject || !selectedKey) return;

      try {
        setExamplesLoading(true);
        setExamplesDrawerOpen(true);
        setStepExamplesData(null);

        const data = await fetchStepExamples(
          currentProject.id,
          fromClusterId,
          toClusterId,
          selectedKey,
          token
        );
        setStepExamplesData(data);
      } catch (err: any) {
        console.error('Error loading step examples:', err);
      } finally {
        setExamplesLoading(false);
      }
    },
    [currentProject, selectedKey, token]
  );

  // Handle topic click from overview - receives full cluster info directly
  const handleOverviewTopicClick = useCallback(
    (cluster: ClusterSummary) => {
      handleSelectTopic(cluster);
    },
    [handleSelectTopic]
  );

  // Handle viewing examples from hover card
  const handleViewExamples = useCallback((clusterId: number) => {
    setSelectedClusterId(clusterId);
    setIsClusterSheetOpen(true);
  }, []);

  // Handle closing cluster sheet
  const handleClusterSheetClose = useCallback(() => {
    setIsClusterSheetOpen(false);
    setSelectedClusterId(null);
  }, []);

  // Handle dropoff click - open deep dive drawer
  const handleDropoffClick = useCallback(
    async (clusterId: number) => {
      if (!currentProject || !selectedKey) return;

      try {
        setDropoffLoading(true);
        setIsDropoffDrawerOpen(true);
        setDropoffDeepDiveData(null);

        const data = await fetchDropoffDeepDive(
          currentProject.id,
          clusterId,
          selectedKey,
          selectedDays,
          token
        );
        setDropoffDeepDiveData(data);
      } catch (err: any) {
        console.error('Error loading dropoff deep dive:', err);
        setError(err.message || 'Failed to load drop-off analysis');
      } finally {
        setDropoffLoading(false);
      }
    },
    [currentProject, selectedKey, selectedDays, token]
  );

  // Initial load
  useEffect(() => {
    if (currentProject) {
      loadAvailableKeys();
    }
  }, [currentProject, loadAvailableKeys]);

  // Load overview when key or days change
  useEffect(() => {
    if (currentProject && selectedKey) {
      loadOverview();
      // Clear exploration when filters change
      setExplorationPath([]);
      setNextStepsData(null);
    }
  }, [currentProject, selectedKey, selectedDays, loadOverview]);

  // Reset state when project changes
  useEffect(() => {
    setOverviewData(null);
    setNextStepsData(null);
    setClusters([]);
    setExplorationPath([]);
    setError(null);
    setAvailableKeys([]);
  }, [currentProject?.id]);

  if (!currentProject) {
    return (
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-muted-foreground">
                <AlertCircle className="h-12 w-12 mx-auto mb-4" />
                <p>Please select a project to view journeys</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-neutral-800">
            <h1 className="text-2xl font-bold flex items-center">
              <GitBranch className="h-6 w-6 mr-2" /> Journeys
            </h1>
            <Badge variant="secondary" className="text-sm">
              {currentProject.name}
            </Badge>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            {/* Time range selector */}
            <Select
              value={selectedDays.toString()}
              onValueChange={(v) => setSelectedDays(parseInt(v) as TimeRangeOption)}
            >
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_RANGE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value.toString()}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Key selector */}
            <Select value={selectedKey} onValueChange={setSelectedKey}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Select key" />
              </SelectTrigger>
              <SelectContent>
                {availableKeys.map((key) => (
                  <SelectItem key={key} value={key}>
                    {key}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Advanced settings */}
            <AdvancedSettings countMode={countMode} onCountModeChange={setCountMode} />

            {/* Filters button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsSegmentsPanelOpen(true)}
              className="h-10 px-3"
            >
              <SlidersHorizontal className="h-4 w-4 mr-2" />
              Filters
              {Object.keys(segmentFilters).length > 0 && (
                <Badge variant="secondary" className="ml-2 text-xs">
                  {Object.keys(segmentFilters).length}
                </Badge>
              )}
            </Button>

            {/* Refresh button */}
            <Button
              variant="outline"
              size="icon"
              onClick={loadOverview}
              disabled={overviewLoading}
            >
              <RefreshCw className={`h-4 w-4 ${overviewLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-red-700">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm font-medium">{error}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setError(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats summary */}
        {overviewData && (
          <div 
            className="flex items-center gap-4 text-sm text-gray-500"
            style={{
              opacity: 0,
              animation: 'cardFadeIn 500ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
            }}
          >
            <span>
              <strong className="text-gray-900">
                {overviewData.stats.total_conversations.toLocaleString()}
              </strong>{' '}
              conversations
            </span>
            <span>|</span>
            <span>
              <strong className="text-gray-900">
                {overviewData.stats.total_transitions.toLocaleString()}
              </strong>{' '}
              transitions
            </span>
            <span>|</span>
            <span>
              {overviewData.stats.date_range.start} to {overviewData.stats.date_range.end}
            </span>
          </div>
        )}

        {/* Overview Section */}
        <OverviewSection
          startingTopics={overviewData?.starting_topics || []}
          commonFollowups={overviewData?.common_followups || []}
          backAndForthPatterns={overviewData?.back_and_forth_patterns || []}
          dropoffPoints={overviewData?.dropoff_points || []}
          onTopicClick={handleOverviewTopicClick}
          onViewExamples={handleViewExamples}
          onDropoffClick={handleDropoffClick}
          explorationPath={explorationPath}
          loading={overviewLoading}
        />

        {/* Topic Explorer Section */}
        <TopicExplorer
          clusters={clusters}
          clustersLoading={clustersLoading}
          nextStepsData={nextStepsData}
          nextStepsLoading={nextStepsLoading}
          explorationPath={explorationPath}
          suggestedTopics={overviewData?.starting_topics || []}
          onSelectTopic={handleSelectTopic}
          onNavigateToPathIndex={handleNavigateToPathIndex}
          onClearPath={handleClearPath}
          onShowExamples={handleShowExamples}
          selectedKey={selectedKey}
          isPathBased={explorationPath.length > 1}
        />

        {/* Examples Drawer */}
        <ExamplesDrawer
          open={examplesDrawerOpen}
          onClose={() => setExamplesDrawerOpen(false)}
          loading={examplesLoading}
          stepExamples={stepExamplesData}
          topicExamples={null}
          mode="step"
        />

        {/* Segments Panel */}
        <SegmentsPanel
          isOpen={isSegmentsPanelOpen}
          onClose={() => setIsSegmentsPanelOpen(false)}
          projectId={currentProject?.id || 0}
          token={token}
          activeFilters={segmentFilters}
          onFiltersChange={setSegmentFilters}
        />

        {/* Cluster Values Sheet (for View Examples) */}
        <ClusterValuesSheet
          isOpen={isClusterSheetOpen}
          onClose={handleClusterSheetClose}
          clusterId={selectedClusterId}
          projectId={currentProject?.id || null}
          token={token}
        />

        {/* Dropoff Deep Dive Drawer */}
        <DropoffDrawer
          open={isDropoffDrawerOpen}
          onClose={() => setIsDropoffDrawerOpen(false)}
          loading={dropoffLoading}
          data={dropoffDeepDiveData}
        />
      </div>
    </div>
  );
}
